import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Phone, Mail, Globe, MapPin, User, GraduationCap, Briefcase, Users, Award } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const RedHexagonTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#E81E31';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#2D2D2D', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+12345678910' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'user@email.com' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'www.website.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'your location here' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit, Lorem i sapien nisi dolore diam'
        : '');
    const photoSrc = data.personalInfo.profileImage || '';

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full relative overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>

            {/* Top Red Wave with Hexagon */}
            {!isContinuationPage ? (
            <div className="relative h-52 overflow-hidden" style={{ backgroundColor: primaryColor }}>
                {/* Decorative wave SVG */}
                <div className="absolute bottom-0 left-0 w-full">
                    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-24">
                        <path d="M0,0 C300,80 600,80 900,0 L1200,0 L1200,120 L0,120 Z" fill="#2D2D2D" opacity="0.3" />
                        <path d="M0,20 C300,100 600,100 900,20 L1200,20 L1200,120 L0,120 Z" fill="#2D2D2D" opacity="0.5" />
                        <path d="M0,40 C300,120 600,120 900,40 L1200,40 L1200,120 L0,120 Z" fill="#2D2D2D" />
                    </svg>
                </div>

                {/* Name and Title */}
                <div className="relative z-10 pt-8 pl-12">
                    <h1 className="cv-fs-name text-6xl font-black text-white uppercase leading-none mb-2">
                        {data.personalInfo.firstName || (showPlaceholders ? 'NAME' : '')} {data.personalInfo.lastName || (showPlaceholders ? 'HERE' : '')}
                    </h1>
                    <p className="cv-fs-profession text-lg font-bold" style={{ color: '#333' }}>
                        {data.personalInfo.profession || (showPlaceholders ? 'graphic designer' : '')}
                    </p>

                </div>

                {/* Hexagon Photo - Top Right */}
                <div className="absolute top-4 right-12 z-20">
                    <div className="relative w-44 h-48">
                        {/* Hexagon shape with clip-path */}
                        <div className="w-full h-full relative" style={{
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                        }}>
                            <div className="absolute inset-0" style={{ backgroundColor: imageBorderColor }}></div>

                            {photoSrc && (
                                <div className="absolute inset-2">
                                    <img
                                        src={photoSrc}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                        style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
                                    />
                                </div>
                            )}
                        </div>
                        {/* White border effect */}
                        <div className="absolute -inset-1 border-4 border-white rounded-3xl opacity-20" style={{
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                        }}></div>
                    </div>
                </div>
            </div>
            ) : (
            <div className="h-10 px-12 flex items-center" style={{ backgroundColor: primaryColor }}>
                <p className="text-sm font-bold uppercase tracking-widest text-white/80">{CV_LABELS.suite}</p>
            </div>
            )}

            <div className="flex">
                {/* Left Sidebar - Dark with organic shapes */}
                <div data-cv-sidebar className="w-[40%] text-white p-10 relative overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>

                    {/* Organic white shape decorations */}
                    <div className="absolute top-20 -right-10 w-64 h-80 bg-white rounded-l-full opacity-10"></div>
                    <div className="absolute bottom-32 -right-8 w-56 h-64 bg-white rounded-l-full opacity-10"></div>

                    {isContinuationPage ? (
                    <div className="mt-4 relative z-10">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                    ) : (
                    <>
                    {/* Contact Section */}
                    {(phone || email || website || address || showPlaceholders) && (
                    <section className="mb-5 relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                <Phone className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-black uppercase tracking-wider">{CV_LABELS.contact}</h2>
                        </div>
                        <div className="cv-fs-contact space-y-2 text-sm pl-1">
                            {phone && <p>{phone}</p>}
                            {email && <p className="break-all">{email}</p>}
                            {website && <p>{website}</p>}
                            {address && <p>{address}</p>}
                        </div>
                    </section>
                    )}

                    {/* Reference Section */}
                    <section className="mb-5 relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-black uppercase tracking-wider">{CV_LABELS.references}</h2>
                        </div>
                        {data.references && data.references.length > 0 ? data.references.slice(0, 1).map((ref, i) => (
                            <div
                                key={ref.id || i}
                                className="cv-fs-references text-sm pl-1"
                                data-cv-block
                                data-cv-item-id={ref.id || undefined}
                            >
                                <p className="font-black text-base mb-1 uppercase">{ref.name}</p>
                                <p className="text-xs mb-2">{ref.position}{ref.company ? ` - ${ref.company}` : ''}</p>
                                {ref.phone && <p className="text-xs">{ref.phone}</p>}
                                {ref.email && <p className="text-xs">{ref.email}</p>}
                            </div>
                        )) : showPlaceholders ? (
                            <div className="cv-fs-references text-sm pl-1">
                                <p className="font-black text-base mb-1 uppercase">JHON SMITH JR</p>
                                <p className="text-xs mb-2">Poste</p>
                                <p className="text-xs">phone number</p>
                                <p className="text-xs">address here</p>
                            </div>
                        ) : null}
                    </section>

                    {/* Skills Section */}
                    <section className="relative z-10">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor="#ffffff" variant="bar" />
                        <div className="cv-fs-skills space-y-2 pl-1">
                            {(data.skills && data.skills.length > 0 ? data.skills.slice(0, 3) : showPlaceholders ? [
                                { name: 'Adobe Illustrator', level: 85 },
                                { name: 'Adobe Photoshop', level: 90 },
                                { name: 'Adobe Illustrator', level: 75 }
                            ] : []).map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <p className="text-sm font-bold mb-2">{skill.name}</p>
                                    <div className="h-2 bg-[#444] rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${skill.level}%`, backgroundColor: primaryColor }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    </>
                    )}
                </div>

                {/* Right Content - White */}
                <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12" style={{ backgroundColor: backgroundColor, color: textColor }}>

                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                    {/* About Me Section */}
                    {summary && (
                    <section className="mb-5">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-2xl font-black uppercase">{CV_LABELS.about}</h2>
                        </div>
                        <p className="cv-fs-summary text-xs leading-relaxed pl-1" style={{ color: textColor, opacity: 0.8 }}>
                            {summary}
                        </p>
                    </section>
                    )}

                    {/* Education Section */}
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="bar" />
                        <div className="cv-fs-education space-y-6 pl-1">
                            {data.education.length > 0 ? data.education.map((edu, i) => (
                                <div
                                    key={edu.id || i}
                                    data-cv-block
                                    data-cv-item-id={edu.id || undefined}
                                >
                                    <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>
                                        {edu.degree} : {edu.startDate} - {frPresent(edu.isCurrent, edu.endDate)}
                                    </p>
                                    <p className="text-xs font-bold mb-2" style={{ color: textColor }}>{edu.institution}</p>
                                    <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>
                                        {edu.degree} in {edu.field}. {edu.description || 'Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.'}
                                    </p>

                                </div>
                            )) : showPlaceholders ? (
                                <>
                                    <div>
                                        <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>Diplôme : 2018-2020</p>
                                        <p className="text-xs font-bold text-slate-900 mb-2">Établissement</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>Diplôme : 2013-2016</p>
                                        <p className="text-xs font-bold text-slate-900 mb-2">Établissement</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.</p>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </section>

                    {/* Experience Section */}
                    <section>
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="bar" />
                        <div className="cv-fs-experience space-y-6 pl-1">
                            {data.experience.length > 0 ? data.experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                >
                                    <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>
                                        {exp.company} : {exp.startDate}-{frPresent(exp.isCurrent, exp.endDate)}
                                    </p>
                                    <p className="text-xs font-bold mb-2" style={{ color: textColor }}>{exp.position}</p>
                                    <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>{exp.description || 'Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.'}</p>

                                </div>
                            )) : showPlaceholders ? (
                                <>
                                    <div>
                                        <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>Entreprise : 2018-2020</p>
                                        <p className="text-xs font-bold text-slate-900 mb-2">Poste</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-black uppercase mb-1" style={{ color: primaryColor }}>Entreprise : 2018-2020</p>
                                        <p className="text-xs font-bold text-slate-900 mb-2">Poste</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">Lorem ipsum dolor sitlora ng elit, Lorem nisi dolore diam Lorem ipsum doloru lorem ng elit.</p>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default RedHexagonTemplate;
